import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, MessageCircle } from "@/components/Icon";

import heroAsset from "@/assets/header-over-ons.webp.asset.json";
import ervaringAsset from "@/assets/over-ons-ervaring.webp.asset.json";
import techZeefdruk from "@/assets/techniques/zeefdruk.webp";
import techTransfer from "@/assets/techniques/transferdruk.webp";
import techBorduren from "@/assets/techniques/borduren.webp";
import techDtf from "@/assets/techniques/dtf.webp";

const heroImg = heroAsset.url;
const SITE_URL = "https://www.magicprint.nl";

const reasons = [
  "Meer dan 30 jaar ervaring",
  "100% eigen productie in Den Haag",
  "Professioneel advies en persoonlijke service",
  "Hoogwaardige druk- en borduurtechnieken",
  "Snelle levertijden",
  "Scherpe prijzen",
  "Groot assortiment kleding en textiel",
  "Betrouwbare partner voor bedrijven, verenigingen en evenementen",
];

const technieken = [
  {
    title: "Zeefdruk",
    desc: "Klassieke druktechniek met heldere, dekkende kleuren en een duurzaam resultaat. Ideaal voor T-shirts, hoodies en polo's.",
    img: techZeefdruk,
    min: "Vanaf 10 stuks",
    href: "/druktechnieken/zeefdrukkerij",
  },
  {
    title: "Transferdruk",
    desc: "Full color bedrukking met heldere kleuren en een haarscherp resultaat. Perfect voor foto's en gedetailleerde ontwerpen.",
    img: techDtf,
    min: "Vanaf 10 stuks",
    href: "/druktechnieken/transferdruk",
  },
  {
    title: "Borduren",
    desc: "Borduren geeft kleding een luxe en duurzame uitstraling. Ideaal voor bedrijfskleding, polo's en sweaters.",
    img: techBorduren,
    min: "Vanaf 25 stuks",
    href: "/druktechnieken/borduren",
  },
  {
    title: "DTF Printing",
    desc: "Met DTF printing kunnen wij gedetailleerde en kleurrijke ontwerpen direct op textiel drukken. Ideaal voor prints met veel kleuren.",
    img: techTransfer,
    min: "Vanaf 10 stuks",
    href: "/druktechnieken/dtf-printing",
  },
];

const assortiment = [
  { name: "T-shirts", href: "/producten/heren" },
  { name: "Polo's", href: "/producten/heren" },
  { name: "Hoodies", href: "/producten/heren" },
  { name: "Sweaters", href: "/producten/heren" },
  { name: "Werkkleding", href: "/producten/workwear" },
  { name: "Sportkleding", href: "/producten/sport" },
  { name: "Caps", href: "/producten/accessoires" },
  { name: "Jassen", href: "/producten/heren" },
  { name: "Horecakleding", href: "/producten/workwear" },
  { name: "Accessoires", href: "/producten/accessoires" },
];

const OverOns = () => (
  <>
    <Helmet>
      <title>Over ons — Magic Print Den Haag | Sinds 1994</title>
      <meta
        name="description"
        content="Al ruim 30 jaar dé specialist in kleding bedrukken en borduren in Den Haag. Eigen productie, persoonlijke service en scherpe prijzen sinds 1994."
      />
      <link rel="canonical" href={`${SITE_URL}/over-ons`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Magic Print" />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:title" content="Over ons — Magic Print Den Haag" />
      <meta
        property="og:description"
        content="Al sinds 1994 dé specialist in textielbedrukking en borduurwerk in Den Haag."
      />
      <meta property="og:url" content={`${SITE_URL}/over-ons`} />
      <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Over ons — Magic Print Den Haag" />
      <meta name="twitter:description" content="Al sinds 1994 dé specialist in textielbedrukking en borduurwerk in Den Haag." />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />

    </Helmet>

    <Navbar />

    <main className="min-h-screen bg-background pb-20">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <img
          src={heroImg}
          alt="Magic Print — textieldrukkerij in Den Haag"
          className="absolute inset-0 w-full h-full object-cover object-top"
          width={1600}
          height={627}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
        <div className="container relative z-10 py-32 md:py-40">
          <div className="max-w-3xl space-y-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider uppercase bg-white text-[#0a2933] shadow-lg">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Over Magic Print — Sinds 1994
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-surface-dark-foreground">
              Al ruim 30 jaar dé specialist in kleding bedrukken en borduren in Den Haag
            </h1>
            <p className="text-lg md:text-xl text-surface-dark-foreground/80 max-w-2xl">
              Wat ooit begon als een passie voor kwaliteit en creativiteit, is uitgegroeid tot een
              modern familiebedrijf waar klantvriendelijkheid, expertise en vakmanschap nog altijd
              de belangrijkste kernwaarden zijn.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="container py-16 md:py-20">
        <div className="space-y-14">
          <section className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
                Meer dan 30 jaar ervaring maakt het verschil
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Wanneer u kleding laat bedrukken of borduren, wilt u zeker weten dat het eindresultaat
                perfect is. Dankzij onze jarenlange ervaring weten wij precies welke druk- of
                borduurtechniek het beste past bij uw ontwerp, het soort textiel en de gewenste oplage.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Of het nu gaat om 10 T-shirts voor een studentenvereniging of duizenden kledingstukken
                voor een landelijke promotiecampagne, iedere opdracht krijgt bij ons dezelfde zorg en
                aandacht.
              </p>
            </div>
            <img
              src={ervaringAsset.url}
              alt="Medewerkers van Magic Print met bedrukte shirts in de drukkerij in Den Haag"
              loading="lazy"
              width={768}
              height={504}
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Eigen productie, maximale kwaliteit
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Bij Magic Print vindt de volledige productie plaats in onze eigen textieldrukkerij in
              Den Haag. Daardoor hebben wij volledige controle over het productieproces en kunnen
              wij een constante hoge kwaliteit garanderen. Er wordt niets uitbesteed. Dat betekent
              korte lijnen, snelle levertijden en een eindproduct waar wij trots op zijn.
            </p>
            <p className="text-muted-foreground leading-relaxed">Wij werken met moderne technieken zoals:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 pt-2">
              {technieken.map((t) => (
                <Link
                  to={t.href}
                  key={t.title}
                  className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/40 hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={t.img}
                      alt={t.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={800}
                      height={600}
                    />
                    <span className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      {t.min}
                    </span>
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="font-heading font-semibold text-xl text-[#0a2933]">{t.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
                    <span className="inline-flex items-center text-primary text-sm font-medium group-hover:text-primary/80">
                      Meer informatie <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Hierdoor kunnen wij vrijwel ieder ontwerp haarscherp en duurzaam op textiel aanbrengen.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Voor bedrijven, verenigingen, evenementen en particulieren
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Door de jaren heen hebben wij duizenden klanten geholpen met het realiseren van hun
              kledingprojecten. Van professionele bedrijfskleding tot sportteams, festivals, scholen,
              horecabedrijven, studentenverenigingen en promotionele acties.
            </p>
            <p className="text-muted-foreground leading-relaxed">In ons assortiment vindt u onder andere:</p>
            <div className="flex flex-wrap gap-2">
              {assortiment.map((a) => (
                <Link
                  key={a.name}
                  to={a.href}
                  className="px-4 py-2 rounded-full bg-primary/10 hover:bg-primary hover:text-primary-foreground text-[#0a2933] text-sm font-medium transition-colors"
                >
                  {a.name}
                </Link>
              ))}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Met duizenden producten en tientallen merken is er altijd een passende oplossing.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Persoonlijke service staat centraal
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Wat klanten vaak het meest waarderen, is onze persoonlijke aanpak. Wij nemen de tijd
              om mee te denken over het ontwerp, de juiste kledingkeuze en de beste druktechniek.
              Geen standaardoplossingen, maar advies op maat.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Vanaf het eerste contact tot de uiteindelijke levering staat één ding centraal: een
              tevreden klant.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Scherpe prijzen zonder concessies aan kwaliteit
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Doordat wij zelf produceren en rechtstreeks inkopen, kunnen wij hoogwaardige kwaliteit
              leveren tegen zeer concurrerende prijzen. Of u nu een kleine oplage nodig heeft of een
              grote bestelling wilt plaatsen, wij bieden altijd een passende oplossing.
            </p>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Waarom kiezen klanten al sinds 1994 voor Magic Print?
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {reasons.map((r) => (
                <li
                  key={r}
                  className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border"
                >
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-[#0a2933] font-medium">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4 p-8 rounded-2xl bg-card border border-border">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a2933]">
              Op zoek naar een betrouwbare textieldrukkerij?
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Bent u op zoek naar een specialist voor het bedrukken of borduren van kleding? Dan
              bent u bij Magic Print aan het juiste adres. Sinds 1994 helpen wij bedrijven,
              organisaties en particulieren met hoogwaardige textielbedrukking die gezien mag worden.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Neem gerust contact met ons op voor vrijblijvend advies of een offerte. Wij denken
              graag met u mee en zorgen ervoor dat uw kleding perfect aansluit bij uw wensen.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild size="lg">
                <Link to="/offerte">
                  Gratis offerte aanvragen <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact">Contact opnemen</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="https://wa.me/31652526002" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 w-4 h-4" /> WhatsApp
                </a>
              </Button>
            </div>
          </section>
        </div>
      </div>
    </main>

    <Footer />
    <WhatsAppButton />
  </>
);

export default OverOns;
